import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { useCart } from "./CartContext";
import { fetchCurrentOrders } from "../services/orderService";

interface OrderNotificationsValue {
  /** Total number of orders requiring user action */
  actionCount: number;
  /** Refresh notification counts (e.g. after completing an action) */
  refresh: () => void;
}

const OrderNotificationsContext = createContext<OrderNotificationsValue>({
  actionCount: 0,
  refresh: () => {},
});

export function useOrderNotifications() {
  return useContext(OrderNotificationsContext);
}

export function OrderNotificationsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthed } = useAuth();
  const { refreshWonAuctions } = useCart();
  const [actionCount, setActionCount] = useState(0);

  const load = useCallback(async () => {
    if (!isAuthed) {
      setActionCount(0);
      return;
    }

    try {
      const current = await fetchCurrentOrders();
      setActionCount(current.action_required.length);
      refreshWonAuctions();
    } catch {
      setActionCount(0);
    }
  }, [isAuthed, refreshWonAuctions]);

  useEffect(() => {
    load();
    // Poll every 60 seconds
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const value = useMemo(
    () => ({ actionCount, refresh: load }),
    [actionCount, load],
  );

  return (
    <OrderNotificationsContext.Provider value={value}>
      {children}
    </OrderNotificationsContext.Provider>
  );
}
